import type {
  AnimationPreset,
  ElementAnimation,
  LoopAnimation,
  TimeRange,
} from "../../scene/schema";
import type {
  DeviceNode,
  ImageNode,
  LogoNode,
  ShapeNode,
  StackNode,
  TemplateNode,
  TextNode,
} from "../../templates/builder";
import type { BlockNode, IconNode } from "../build";

/**
 * Shorthands for authoring the built-in blocks (docs/04-dokumentformat.md
 * "Bausteine"). Coordinates are relative to the block on the reference scale
 * (shorter page side = 1080 px); colours and text styles are brand roles.
 * Node times are relative to the block's start and end with it.
 */

type Props<T> = Omit<T, "kind">;

export const text = (props: Props<TextNode>): TextNode => ({ kind: "text", ...props });
export const shape = (
  kind: ShapeNode["shape"],
  props: Omit<ShapeNode, "kind" | "shape">,
): ShapeNode => ({
  kind: "shape",
  shape: kind,
  ...props,
});
export const rect = (props: Omit<ShapeNode, "kind" | "shape">) => shape("rect", props);
export const ellipse = (props: Omit<ShapeNode, "kind" | "shape">) => shape("ellipse", props);
export const photo = (props: Props<ImageNode>): ImageNode => ({ kind: "image", ...props });
export const logo = (props: Props<LogoNode>): LogoNode => ({ kind: "logo", ...props });
export const stack = (props: Props<StackNode>): StackNode => ({ kind: "stack", ...props });
export const icon = (props: Props<IconNode>): IconNode => ({ kind: "icon", ...props });
/** A device mockup with an empty screen; give `w` or `h`, the other side follows the device. */
export const device = (props: Props<DeviceNode>): DeviceNode => ({ kind: "device", ...props });

/** A straight line of `width` from (x, y) to the right (turn it with `rotation`). */
export const line = (
  x: number,
  y: number,
  w: number,
  color: string,
  width: number,
  extra: Partial<Pick<ShapeNode, "time" | "animation" | "rotation" | "name">> & {
    dash?: "solid" | "dashed" | "dotted";
  } = {},
): ShapeNode => {
  const { dash, ...rest } = extra;
  return {
    kind: "shape",
    shape: "line",
    x,
    y: y - 9,
    w,
    h: 18,
    stroke: { color, width, ...(dash ? { dash } : {}) },
    name: "Linie",
    ...rest,
  };
};

/** Far end: the builder cuts every time at the block's end. */
export const END = 24 * 60 * 60 * 1000;

export interface MotionOptions {
  /** Appears this long after the block (ms). */
  delay?: number;
  /** Disappears this long after the block's start (ms); default: with the block. */
  until?: number;
  duration?: number;
  /** Exit (default: fade). `null`: none. */
  out?: AnimationPreset | null;
  outDuration?: number;
  loop?: LoopAnimation;
}

/** Entrance (and exit) of a node, optionally delayed or ending early. */
export function motion(
  entrance: AnimationPreset,
  options: MotionOptions = {},
): { time?: TimeRange; animation: ElementAnimation } {
  const out = options.out === undefined ? "fade" : options.out;
  const timed = options.delay || options.until !== undefined;
  return {
    ...(timed ? { time: { start: options.delay ?? 0, end: options.until ?? END } } : {}),
    animation: {
      in: { preset: entrance, duration: options.duration ?? 500 },
      ...(out ? { out: { preset: out, duration: options.outDuration ?? 300 } } : {}),
      ...(options.loop ? { loop: options.loop } : {}),
    },
  };
}

export type Extra = Partial<Pick<ShapeNode, "time" | "animation" | "rotation">>;

/** A pill or button: shape plus centred label (readable on brand colours; `color` for fixed fills). */
export function pill(
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  fill: string,
  size: number,
  move: Extra = {},
  options: {
    style?: TextNode["style"];
    color?: string;
    radius?: ShapeNode["radius"];
    stroke?: ShapeNode["stroke"];
  } = {},
): [ShapeNode, TextNode] {
  return [
    rect({
      x,
      y,
      w,
      h,
      fill,
      radius: options.radius ?? "full",
      name: label,
      ...(options.stroke ? { stroke: options.stroke } : {}),
      ...move,
    }),
    text({
      text: label,
      style: options.style ?? "subline",
      size,
      color: options.color ?? `on:${fill.split("/")[0]}`,
      x: x + h * 0.3,
      y,
      w: w - h * 0.6,
      h,
      align: "center",
      ...move,
    }),
  ];
}

/**
 * A pill with an icon on the left and a label: the icon sits in a circle of
 * `badge` (without it straight on the pill) and the label reads on `fill`.
 * Fixed (`#…`) fills need an explicit `color`.
 */
export function iconPill(
  x: number,
  y: number,
  w: number,
  h: number,
  options: {
    icon: IconNode["icon"];
    label: string;
    fill: string;
    size: number;
    badge?: string;
    move?: Extra;
    iconMove?: Extra;
    color?: string;
    iconColor?: string;
    style?: TextNode["style"];
  },
): BlockNode[] {
  const move = options.move ?? {};
  const inset = h * 0.12;
  const circle = h - inset * 2;
  const badge = options.badge;
  const color = options.color ?? `on:${options.fill.split("/")[0]}`;
  const iconColor =
    options.iconColor ?? (badge && !badge.startsWith("#") ? `on:${badge.split("/")[0]}` : color);
  return [
    rect({ x, y, w, h, fill: options.fill, radius: "full", name: options.label, ...move }),
    ...(badge
      ? [
          ellipse({
            x: x + inset,
            y: y + inset,
            w: circle,
            h: circle,
            fill: badge,
            name: "Kreis",
            ...(options.iconMove ?? move),
          }),
        ]
      : []),
    icon({
      icon: options.icon,
      x: x + inset + circle * 0.2,
      y: y + inset + circle * 0.2,
      w: circle * 0.6,
      h: circle * 0.6,
      color: iconColor,
      name: "Symbol",
      ...(options.iconMove ?? move),
    }),
    text({
      text: options.label,
      style: options.style ?? "subline",
      size: options.size,
      color,
      x: x + h + inset,
      y,
      w: w - h - inset - h * 0.4,
      h,
      align: "center",
      ...move,
    }),
  ];
}

export const WHITE = "#ffffff";
export const BLACK_GLASS = "#000000b3";
export const SHADOW = { color: "#00000080", y: 4, blur: 20 };
/** Soft shadow under cards and buttons. */
export const CARD_SHADOW = { color: "#00000033", y: 12, blur: 40 };

export type { TemplateNode };
